/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import React from 'react';
import { Row, Col } from 'reactstrap';
import {
  RESIDUAL_DRIFT_DISPERSION_DIR_1,
  RESIDUAL_DRIFT_DISPERSION_DIR_2,
  RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_1,
  RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_2,
  RESIDUAL_DRIFT_MODEL_DIR_1,
  RESIDUAL_DRIFT_MODEL_DIR_2,
  RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_1,
  RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_2,
  STRUCTURAL_RESPONSE_DERIVED_FROM,
  STRUCTURAL_RESPONSE_METHOD,
  STRUCTURAL_RESPONSE_NAME,
  STRUCTURAL_RESPONSE_USE_TO_DEFAULT_COMPONENTS,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import {
  Field,
  Fieldset,
  FormGroup,
  Label,
} from '#components/pages/Models/Model/support/form/index.js';
import { edpsByKey } from '#constants/edps/index.js';
import type { EdpKey } from '#constants/edps/index.js';
import StructuralResponseTable from '#components/pages/Models/Model/sections/type2/StructuralResponsesSection/EditView/Response/ResponseDetail/Table/index.js';
import StructuralResponsePlot from '#components/pages/Models/Model/sections/type2/StructuralResponsesSection/EditView/Response/ResponseDetail/Plot/index.js';
import StructuralResponseUploader from './Uploader/index.js';

type EdpResponseProps = {
  modelId: string,
  form: string,
  field: string,
  responseItemKey: string,
  edpKey: EdpKey,
  method: string,
  showUploader: boolean,
  showResponse: boolean,
  itemSchemaFieldsByName: Object,
  methodOptions: Array<Object>,
  idrResponseOptions: Array<Object>,
};

const ResponseDetail = ({
  form,
  modelId,
  field,
  edpKey,
  responseItemKey,
  method,
  showResponse,
  showUploader,
  itemSchemaFieldsByName,
  methodOptions,
  idrResponseOptions,
}: EdpResponseProps): Element<'div'> => {
  const edp = edpsByKey[edpKey];
  const plotDir1 = showResponse ? (
    <Row>
      <Col>
        <StructuralResponsePlot
          modelId={modelId}
          responseItemKey={responseItemKey}
          edpKey={edpKey}
          direction={1}
        />
      </Col>
    </Row>
  ) : null;
  const plotDir2 = showResponse ? (
    <Row>
      <Col>
        <StructuralResponsePlot
          modelId={modelId}
          responseItemKey={responseItemKey}
          edpKey={edpKey}
          direction={2}
        />
      </Col>
    </Row>
  ) : null;
  const tableDir1 = showResponse ? (
    <Row>
      <Col>
        <StructuralResponseTable
          modelId={modelId}
          responseItemKey={responseItemKey}
          edpKey={edpKey}
          direction={1}
        />
      </Col>
    </Row>
  ) : null;
  const tableDir2 = showResponse ? (
    <Row>
      <Col>
        <StructuralResponseTable
          modelId={modelId}
          responseItemKey={responseItemKey}
          edpKey={edpKey}
          direction={2}
        />
      </Col>
    </Row>
  ) : null;
  return (
    <div className={`${edpKey}-response-detail-view`} key={`response-${responseItemKey}`}>
      <h3>
        {edp.name} <Tip tipId={edpKey} />
      </h3>
      <FormGroup row>
        <Col lg="5">
          <Label field={itemSchemaFieldsByName[STRUCTURAL_RESPONSE_NAME]}>
            {itemSchemaFieldsByName[STRUCTURAL_RESPONSE_NAME].label}
          </Label>
          <Field
            item={field}
            field={itemSchemaFieldsByName[STRUCTURAL_RESPONSE_NAME]}
            excludeLabel
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Col lg="5">
          <Label field={itemSchemaFieldsByName[STRUCTURAL_RESPONSE_METHOD]}>
            {itemSchemaFieldsByName[STRUCTURAL_RESPONSE_METHOD].label}
          </Label>
          <Field
            item={field}
            field={{
              ...itemSchemaFieldsByName[STRUCTURAL_RESPONSE_METHOD],
              options: methodOptions,
            }}
            excludeLabel
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Col lg="5">
          <Label field={itemSchemaFieldsByName[STRUCTURAL_RESPONSE_USE_TO_DEFAULT_COMPONENTS]}>
            {itemSchemaFieldsByName[STRUCTURAL_RESPONSE_USE_TO_DEFAULT_COMPONENTS].label}
          </Label>
          <Field
            item={field}
            field={itemSchemaFieldsByName[STRUCTURAL_RESPONSE_USE_TO_DEFAULT_COMPONENTS]}
            excludeLabel
          />
        </Col>
      </FormGroup>
      <FormGroup row form={form} fields={[`${field}.${STRUCTURAL_RESPONSE_DERIVED_FROM}`]}>
        <Col lg="5">
          <Label field={itemSchemaFieldsByName[STRUCTURAL_RESPONSE_DERIVED_FROM]}>
            {itemSchemaFieldsByName[STRUCTURAL_RESPONSE_DERIVED_FROM].label}
          </Label>
          <Field
            item={field}
            field={{
              ...itemSchemaFieldsByName[STRUCTURAL_RESPONSE_DERIVED_FROM],
              options: idrResponseOptions,
            }}
            excludeLabel
          />
        </Col>
      </FormGroup>
      {showUploader && (
        <StructuralResponseUploader
          form={form}
          name={`${field}.upload`}
          modelId={modelId}
          edpKey={edpKey}
          method={method}
          responseItemKey={responseItemKey}
          accept=".csv"
          maxBytes={1.6e7}
        />
      )}
      <div>
        <Fieldset
          form={form}
          fields={[
            `${field}.${RESIDUAL_DRIFT_MODEL_DIR_1}`,
            `${field}.${RESIDUAL_DRIFT_DISPERSION_DIR_1}`,
            `${field}.${RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_1}`,
            `${field}.${RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_1}`,
          ]}
        >
          <legend>Direction 1</legend>
          <FormGroup row form={form} fields={[`${field}.${RESIDUAL_DRIFT_MODEL_DIR_1}`]}>
            <Col lg="5">
              <Label field={itemSchemaFieldsByName[RESIDUAL_DRIFT_MODEL_DIR_1]}>
                {itemSchemaFieldsByName[RESIDUAL_DRIFT_MODEL_DIR_1].label}
              </Label>
              <Field
                item={field}
                field={itemSchemaFieldsByName[RESIDUAL_DRIFT_MODEL_DIR_1]}
                excludeLabel
              />
            </Col>
          </FormGroup>
          <FormGroup
            row
            form={form}
            fields={[`${field}.${RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_1}`]}
          >
            <Col lg="5">
              <Label field={itemSchemaFieldsByName[RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_1]}>
                {itemSchemaFieldsByName[RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_1].label}
              </Label>
              <Field
                item={field}
                field={itemSchemaFieldsByName[RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_1]}
                excludeLabel
              />
            </Col>
          </FormGroup>
          <FormGroup row form={form} fields={[`${field}.${RESIDUAL_DRIFT_DISPERSION_DIR_1}`]}>
            <Col lg="5">
              <Label field={itemSchemaFieldsByName[RESIDUAL_DRIFT_DISPERSION_DIR_1]}>
                {itemSchemaFieldsByName[RESIDUAL_DRIFT_DISPERSION_DIR_1].label}
              </Label>
              <Field
                item={field}
                field={itemSchemaFieldsByName[RESIDUAL_DRIFT_DISPERSION_DIR_1]}
                excludeLabel
              />
            </Col>
          </FormGroup>
          <FormGroup
            row
            form={form}
            fields={[`${field}.${RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_1}`]}
          >
            <Col lg="5">
              <Label
                field={itemSchemaFieldsByName[RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_1]}
              >
                {itemSchemaFieldsByName[RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_1].label}
              </Label>
              <Field
                item={field}
                field={itemSchemaFieldsByName[RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_1]}
                excludeLabel
              />
            </Col>
          </FormGroup>
        </Fieldset>
        {plotDir1}
        {tableDir1}
        <Fieldset
          form={form}
          fields={[
            `${field}.${RESIDUAL_DRIFT_MODEL_DIR_2}`,
            `${field}.${RESIDUAL_DRIFT_DISPERSION_DIR_2}`,
            `${field}.${RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_2}`,
            `${field}.${RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_2}`,
          ]}
        >
          <legend>Direction 2</legend>
          <FormGroup row form={form} fields={[`${field}.${RESIDUAL_DRIFT_MODEL_DIR_2}`]}>
            <Col lg="5">
              <Label field={itemSchemaFieldsByName[RESIDUAL_DRIFT_MODEL_DIR_2]}>
                {itemSchemaFieldsByName[RESIDUAL_DRIFT_MODEL_DIR_2].label}
              </Label>
              <Field
                item={field}
                field={itemSchemaFieldsByName[RESIDUAL_DRIFT_MODEL_DIR_2]}
                excludeLabel
              />
            </Col>
          </FormGroup>
          <FormGroup
            row
            form={form}
            fields={[`${field}.${RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_2}`]}
          >
            <Col lg="5">
              <Label field={itemSchemaFieldsByName[RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_2]}>
                {itemSchemaFieldsByName[RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_2].label}
              </Label>
              <Field
                item={field}
                field={itemSchemaFieldsByName[RESIDUAL_DRIFT_DISTRIBUTION_MODEL_DIR_2]}
                excludeLabel
              />
            </Col>
          </FormGroup>
          <FormGroup row form={form} fields={[`${field}.${RESIDUAL_DRIFT_DISPERSION_DIR_2}`]}>
            <Col lg="5">
              <Label field={itemSchemaFieldsByName[RESIDUAL_DRIFT_DISPERSION_DIR_2]}>
                {itemSchemaFieldsByName[RESIDUAL_DRIFT_DISPERSION_DIR_2].label}
              </Label>
              <Field
                item={field}
                field={itemSchemaFieldsByName[RESIDUAL_DRIFT_DISPERSION_DIR_2]}
                excludeLabel
              />
            </Col>
          </FormGroup>
          <FormGroup
            row
            form={form}
            fields={[`${field}.${RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_2}`]}
          >
            <Col lg="5">
              <Label
                field={itemSchemaFieldsByName[RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_2]}
              >
                {itemSchemaFieldsByName[RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_2].label}
              </Label>
              <Field
                item={field}
                field={itemSchemaFieldsByName[RESIDUAL_DRIFT_RECORD_TO_RECORD_DISPERSION_DIR_2]}
                excludeLabel
              />
            </Col>
          </FormGroup>
        </Fieldset>
        {plotDir2}
        {tableDir2}
      </div>
    </div>
  );
};

export default ResponseDetail;
